import React, { useMemo } from 'react';
import { Button, ButtonGroup, Modal, PaginationTable } from '@ver-uds/react';
import { Alert } from '@ver-uds/uswds-react';
import { useAuthState } from '@vis-auth/react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { EveRoutes } from '@ver-uds/e-verify';
import { AccountType, HiringSitesStates } from 'eve-account-client';
import type { Column } from 'react-table';
import { InternalRoutes } from '../../../constants';
import AlertConstants from '../../../constants/AlertConstants';
import ErrorConstants from '../../../constants/ErrorConstants';
import { useStatesQuery, useAccountQuery, useHiringSitesSyncMutation } from '../../../services';
import { scrollToTop } from '../../../utils/util';

const legacyHiringSitesColumns: Column<{ state: string | undefined; count: number }>[] = [
  {
    Header: 'State',
    isRowHeader: true,
    accessor: 'state',
    id: 'state',
  },
  {
    Header: 'Number of Hiring Sites',
    accessor: 'count',
  },
];

export type DeleteLegacyHiringSitesModalProps = {
  open: boolean;
  close(): void;
  numberOfLegacySites: number;
  sitesByState?: HiringSitesStates[];
};

const DeleteLegacyHiringSitesModal = ({
  open,
  close,
  numberOfLegacySites,
  sitesByState,
}: DeleteLegacyHiringSitesModalProps): JSX.Element => {
  const { user } = useAuthState();
  const { subAccountId } = useParams<{ subAccountId?: string }>();

  // eslint-disable-next-line camelcase
  const accountId = subAccountId || (user?.account_id as string);
  const { data: statesData } = useStatesQuery({ enabled: open });
  const { data: accountData } = useAccountQuery(accountId);
  const mappedData = useMemo(
    () =>
      open && statesData
        ? sitesByState?.map((value) => ({
            state: statesData?.find((state) => state.code === value.stateCode)?.displayName,
            count: value.count,
          }))
        : [],
    [statesData, sitesByState, open],
  );

  let nextUrl: string = EveRoutes.COMPANY_HIRING_SITES_EDIT;
  let cancelUrl: string = InternalRoutes.PROFILE;

  if (subAccountId) {
    const nextUrlPath =
      accountData?.type === AccountType.CHILD
        ? EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_HIRING_SITES_EDIT
        : EveRoutes.COMPANY_CLIENTS_CLIENT_HIRING_SITES_EDIT;
    nextUrl = generatePath(nextUrlPath, { subAccountId: accountId });

    const cancelUrlPath =
      accountData?.type === AccountType.CHILD
        ? EveRoutes.CORPORATE_COMPANY_LOCATIONS_CHILD_PROFILE_VIEW
        : EveRoutes.COMPANY_CLIENTS_CLIENT_PROFILE_VIEW;
    cancelUrl = generatePath(cancelUrlPath, { subAccountId: accountId });
  }

  const companyInfoData = accountData?.companyInfo;
  const companyName = companyInfoData?.companyName;
  const history = useHistory();
  const [mutate] = useHiringSitesSyncMutation();

  async function onDeleteLegacyHiringSites() {
    try {
      await mutate({
        accountId,
      });

      close();
      history.push(nextUrl, numberOfLegacySites);
    } catch (error) {
      close();
      history.push(cancelUrl, ErrorConstants.GENERAL_ERROR);
      scrollToTop();
    }
  }

  function closeToProfile() {
    history.push(cancelUrl);
    close();
  }

  const ModalFooter = (): JSX.Element | null => {
    return (
      <Modal.Footer>
        <ButtonGroup>
          <Button type="button" variant="secondary" onClick={(): void => closeToProfile()}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={onDeleteLegacyHiringSites}>
            Yes, Delete Hiring Sites without Address and Proceed
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    );
  };

  return (
    <Modal
      id="delete-legacy-hiring-sites-modal"
      header="Hiring Sites Policy Change"
      open={open}
      close={close}
      fixedToTop
    >
      <Modal.Content>
        <Alert status="warning" className="margin-bottom-2">
          <Alert.Text>
            {AlertConstants.DELETE_LEGACY_HIRING_SITES.replace('{companyName}', companyName || '').replace(
              '{numberOfLegacySites}',
              String(numberOfLegacySites),
            )}
          </Alert.Text>
        </Alert>
        {mappedData && (
          <PaginationTable
            id="legacy-hiring-sites"
            sortable
            variant="small"
            columns={legacyHiringSitesColumns}
            data={mappedData}
          />
        )}
      </Modal.Content>
      <ModalFooter />
    </Modal>
  );
};

// istanbul ignore if: Not used for test environment; Modal.setAppElement should be called in the tests directly.
if (process.env.NODE_ENV !== 'test') {
  Modal.setAppElement('#root');
}

export default DeleteLegacyHiringSitesModal;
